import { clearConsumedCart } from "@/libs/global-e/consumed-cart";
import { onGeSdkEvent } from "@/libs/global-e/sdk-events";
import { markRecentGeOrder } from "@/libs/global-e/recent-ge-order";
import { trackGePurchase } from "@/libs/analytics/track-ge-purchase";
import {
  buildGePurchasePayload,
  GeCheckoutDetails,
} from "@/libs/global-e/checkout-step-payload";
import {
  reportGeClientEvent,
  toGeClientEvent,
} from "@/libs/global-e/client-event";

// Global-E 국제 체크아웃 결제 성공 시 **카트를 비우고 Meta/Ads 전환을 발화한다**.
// 정책/근거: docs/global-e-storefront.md §4.7 (GE "Clearing the Cart" + "Client Analytics").
//
// 문제: GE 체크아웃은 `/international-checkout` 의 IFRAME **안에서** 완료되므로, 우리 주문완료
// 페이지(`src/pages/checkout/[order-id].tsx` 의 `useMount(clearCart)`)를 안 탄다 → 결제 후에도
// 카트가 남는다. GE 는 결제 성공 시 **부모 창**의 `OnCheckoutStepLoaded`(CONFIRMATION) 이벤트로
// 알려주므로(GE SDK), 그 콜백에서 카트를 비운다.
//
// ⚠️ **결제가 이 페이지를 떠난 채 끝나면 이 콜백이 오지 않는다**(GE 문서: "payment redirects can affect analytics
// reporting"). dev 에서 2026-09-29 무렵~10-01 PayPal 이 그랬다 — GE 복귀 주소 설정 문제로 사이트 루트로 돌아왔고 GE 가
// 고쳤다(지금은 체크아웃 페이지로 돌아와 이 콜백이 온다). 그런 경우는 서버 판정 회수(`consumed-cart.ts`, §4.7.3)가 비운다.
// 이 콜백이 정상 경로이고 회수는 안전망이다.
//
// 안전(SendOrderToMerchant 미파손): 우리 백엔드는 GE `SendOrderToMerchant` payload 로 주문을
// 생성하며 **프론트 카트에 비의존**이다 → 프론트 카트 clear 가 주문 생성을 깨뜨리지 않는다.
// GE 문서가 권장하는 "first option"(프론트에서 clear).
//
// 등록(큐 이름 `glegem`·`gle` 둘 다 · 큐 초기화 패턴)은 `sdk-events.ts` 의 `onGeSdkEvent` 가 한다.
// 두 큐에 다 걸리므로 콜백은 멱등이어야 한다 — `clearConsumedCart()` 는 idempotent 라 중복 발화도 안전.
//
// 이 콜백은 「결제창이 떴다」는 신호이기도 하다 — `/international-checkout` 이 스텝 이벤트를 구독해 실패 안내를
// 띄우거나 거둔다(`src/libs/global-e/checkout-load-watch.ts`, docs/global-e-storefront.md §4.4). GE SDK 에 같은 이벤트를 **한 번 더
// 등록하지 않고** 여기서 나눠 준다 — 두 번째 등록이 첫 핸들러(카트 비우기)를 덮는지 GE 문서에 없다.

const stepListeners = new Set<() => void>();

/**
 * GE 체크아웃 스텝 이벤트(어떤 스텝이든)를 구독한다. 반환값으로 해지한다.
 * 구독자는 결제 흐름 밖이라 오류를 삼킨다 — 카트 비우기·구매 발화를 막지 않는다.
 */
export function subscribeGeCheckoutStep(listener: () => void): () => void {
  stepListeners.add(listener);
  return () => {
    stepListeners.delete(listener);
  };
}

function notifyStepListeners() {
  stepListeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      console.warn("[GlobalE] checkout step listener failed", e);
    }
  });
}

interface GeCheckoutStepData {
  /** 현재 스텝 id. LOADED=0 / CONFIRMATION=4. */
  StepId?: number;
  /** 스텝 enum (문서 기준 LOADED:0, CONFIRMATION:4). 하드코딩 대신 이 값과 비교. */
  Steps?: { LOADED?: number; CONFIRMATION?: number };
  /** CONFIRMATION 에서 결제 성공 여부. */
  IsSuccess?: boolean;
  /** confirmation 페이지 새로고침이면 true → 재-clear 방지. */
  IsPageReload?: boolean;
  /**
   * GE 주문 ID (예: `GE178640148JP`). 우리 order ID 아님.
   *
   * ⚠️ **`OrderID` 가 아니라 `OrderId` 다.** 2026-09-17 dev 실측 전까지 이 타입이 `OrderID` 로
   * 적혀 있었고 값은 늘 `undefined` 였다 — 로그에만 쓰여서 아무도 몰랐다. #5789 가 그걸 기능
   * 입력으로 승격시키자 **purchase 가 0건**이 됐다(#5778). GE 공식 샘플·리포의 다른 GE 표면
   * 40여 곳이 전부 `OrderId` 다.
   */
  OrderId?: string;
  /**
   * CONFIRMATION 주문 상세. 통화·금액·아이템이 전부 여기 있고 **PII 는 없다**(주소·이메일 null).
   * 구조와 금액 그룹 선택 규칙은 `checkout-step-payload.ts` 헤더 주석.
   */
  details?: GeCheckoutDetails;
}

function onCheckoutStepLoaded(data: GeCheckoutStepData) {
  // details(고객 주소/이메일 등 PII 포함)는 로깅 금지 — 비-PII 필드만.
  console.log(
    "[GlobalE] OnCheckoutStepLoaded step:",
    data?.StepId,
    "success:",
    data?.IsSuccess,
  );

  // 어떤 스텝이든 결제창이 살아 있다는 뜻이다 — 카트 비우기보다 먼저 알린다(구독자 오류는 위에서 삼킨다).
  notifyStepListeners();

  // CONFIRMATION(결제 승인, 최초 표시)에서만 clear. LOADED/실패/새로고침엔 하지 않는다.
  // CONFIRMATION StepId=4 (GE Client Analytics 정본: LOADED=0/CONFIRMATION=4). payload 에 `Steps`
  // enum 이 없을 때(SDK 변형)도 죽지 않게 문서값 4 로 폴백 — 안 하면 `4 === undefined` = false 로
  // 카트가 영영 안 비워지는 silent no-op(이 fix 가 없애려던 증상 재발). `typeof number` 가드로
  // StepId 결측 이벤트의 오탐 clear(`undefined === undefined`)도 차단. (리뷰 P2)
  const CONFIRMATION = data?.Steps?.CONFIRMATION ?? 4;
  const isConfirmation =
    typeof data?.StepId === "number" && data.StepId === CONFIRMATION;
  if (isConfirmation && data?.IsSuccess === true && !data?.IsPageReload) {
    console.log(
      "[GlobalE] international checkout confirmed → clearing cart (order:",
      data?.OrderId,
      ")",
    );
    // 카트를 비우고 token 을 교체한다(다음 카트 = 새 token). 리다이렉트 결제 회수 경로와 **같은 동작**을 쓴다
    // (`consumed-cart.ts`) — 카트가 이미 비어 있는 바로 결제에서도 token 이 바뀌고, 바로구매였으면(표식 `direct`)
    // **사지 않은 카트는 남긴다**(종전엔 여기서 통째로 비웠다). 표식도 여기서 정리된다.
    // ⚠️ 옛 token 의 서버 guest-cart 는 비우지 않는다 — token 쿠키가 먼저 지워져 빈 sync 가 보낼 곳이 없다(종전 주석
    //    「서버에도 syncCart([]) 전파」는 코드와 달랐다). 옛 token 문서는 쓰이지 않다가 TTL 로 사라진다.
    // 동기 구간(token 교체·비우기)은 아래 발화보다 먼저 끝난다 — async 인 건 뒤따르는 sync flush 뿐이다.
    // 주문 목록이 이 주문을 기다려 다시 읽게 남긴다 — 우리 주문 생성(SendOrder)은 이 화면 뒤에 온다(#6140).
    markRecentGeOrder(String(data?.OrderId ?? ""));
    clearConsumedCart().catch((e) =>
      console.warn("[GlobalE] cart clear after confirmation failed", e),
    );
    // Meta `Purchase` · Ads 전환 (#5778). GA4 는 mgmt 서버사이드 MP 소관 — 여기서 쏘지 않는다.
    // 분석 실패가 결제 완료 화면을 깨뜨리지 않도록 clear **뒤**에 두고 throw 를 접는다.
    firePurchase(data);
  }
}

/**
 * CONFIRMATION payload → Meta/Ads 발화. 실패는 조용히 접는다(분석이 결제를 깨뜨리면 안 된다).
 *
 * 서버 왕복이 없다 — `details` 에 통화·금액·아이템이 전부 있다. 초판(#5789)은 이걸 모르고
 * 라우트로 `GetOrdersDetails` 를 다시 조회했고, 그 라우트는 소유권 게이트·존재 오라클 방어까지
 * 달고 있었다. 전부 불필요해서 되돌렸다(#5796).
 */
function firePurchase(data: GeCheckoutStepData) {
  try {
    const { payload, warnings } = buildGePurchasePayload(
      String(data?.OrderId ?? ""),
      data?.details,
    );
    // ★경고는 payload 유무와 **무관하게** 먼저 찍는다 — 드롭 사유가 `payload:null` 에 묻히면
    // "왜 안 나갔나"를 운영에서 역추적할 수 없다.
    if (warnings.length > 0) {
      // PII 는 남기지 않는다 — 경고엔 sku/통화/금액만 들어간다.
      console.warn("[GlobalE] purchase payload warnings:", warnings);
    }
    if (!payload) {
      console.warn(
        `[GlobalE] purchase payload unavailable (orderId=${data?.OrderId ?? "none"}) — not fired`,
      );
      return;
    }
    trackGePurchase(payload);
  } catch (e) {
    console.warn("[GlobalE] purchase tracking failed", e);
  }
}

let registered = false;
let failureReportRegistered = false;

/**
 * GE `OnClientEvent` 중 결제 실패(`CHECKOUT_FAILURE`)·서버 오류(`ON_SERVER_ERROR`)를 서버 로그로 보고한다(#5807).
 * 규칙은 `src/libs/global-e/client-event.ts`. ⚠️ GE 가 실제로 쏘는지는 미실측 — 오면 남고, 안 오면 그만이다.
 */
export function registerGeCheckoutFailureReport() {
  if (typeof window === "undefined" || failureReportRegistered) return;
  failureReportRegistered = true;

  onGeSdkEvent("OnClientEvent", (source: unknown, data: unknown) => {
    try {
      const constants = (
        window as unknown as {
          GEMerchantUtils?: { ClientEvents?: Record<string, unknown> };
        }
      ).GEMerchantUtils?.ClientEvents;
      const event = toGeClientEvent(source, data, constants);
      if (event) reportGeClientEvent(event);
    } catch {
      // 관측 코드가 결제 화면을 깨면 안 된다
    }
  });
}

/**
 * GE 국제 체크아웃 결제 성공 시 카트 clear 콜백을 등록한다.
 * `GlobalEScripts` 가 GE 활성(flag on) 시 1회 호출. flag off(prod 현재)면 미호출 → 완전 inert.
 * 모듈 싱글턴 가드로 중복 등록 방지(SPA 내 재호출 무해, full reload 시 재등록).
 */
export function registerGeCheckoutCartClear() {
  if (typeof window === "undefined" || registered) return;
  registered = true;

  onGeSdkEvent("OnCheckoutStepLoaded", onCheckoutStepLoaded);
}
